// @flow
import fp from "lodash/fp"
import { createSelector } from "reselect"
import type { PassengerCategory } from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"

import constants from "common/consts/config"
import Price from "common/records/Price"
import {
  getCombinations,
  getAdultHandBagCombinations,
  getAdultHoldBagCombinations,
  getDefinitions,
  getHandBagDefinitions,
  getHoldBagDefinitions,
  getOriginalBaggage,
  getHoldBagCombinations,
} from "../reducer"
import { passengersValuesSelector } from "../../reservation/selectors/passengersSelectors"
import { countBaggage, calculatePriceBaggage, sumBaggagePrices } from "../utils/selectorUtils"
// Keep separate for future logic extension
import { mapperSaveBooking } from "../mapper/mapperSaveBooking"
import { getJourney } from "../../journey/reducer"
import { getAirlines } from "common/services/staticData"
import { getGroupCombinations } from "../utils/baggageUtils"

// eslint-disable-next-line no-unused-vars
export const hasBaggageEnabledSelector = (state: any) => true

// ===============
// Count selectors

export const personalItemBaggageCountSelector: $FlowFixMe = createSelector(
  getCombinations,
  passengersValuesSelector,
  getDefinitions,
  (combinations, passengersValues, definitions) =>
    countBaggage("handBag", "personalItem")(combinations, passengersValues, definitions),
)

export const cabinBaggageCountSelector: $FlowFixMe = createSelector(
  getCombinations,
  passengersValuesSelector,
  getDefinitions,
  countBaggage("handBag", "cabinBag"),
)

export const holdBaggageCountSelector: $FlowFixMe = createSelector(
  getCombinations,
  passengersValuesSelector,
  getDefinitions,
  (combinations, passengersValues, definitions) =>
    countBaggage("holdBag", "holdBag")(combinations, passengersValues, definitions),
)

export const personalItemTotalCountSelector: $FlowFixMe = createSelector(
  personalItemBaggageCountSelector,
  fp.compose(
    fp.sum,
    fp.values,
  ),
)

export const cabinBagTotalCountSelector: $FlowFixMe = createSelector(
  cabinBaggageCountSelector,
  fp.compose(
    fp.sum,
    fp.values,
  ),
)

export const holdBagTotalCountSelector: $FlowFixMe = createSelector(
  holdBaggageCountSelector,
  fp.compose(
    fp.sum,
    fp.values,
  ),
)

export const baggageTotalCountSelector: $FlowFixMe = createSelector(
  personalItemTotalCountSelector,
  cabinBagTotalCountSelector,
  holdBagTotalCountSelector,
  (personalItem, cabinBag, holdBag) => personalItem + cabinBag + holdBag,
)

// ===============
// Price selectors

// Price count
export const personalItemPriceCountSelector: $FlowFixMe = createSelector(
  personalItemBaggageCountSelector,
  getHandBagDefinitions,
  calculatePriceBaggage,
)

export const cabinBagPriceCountSelector: $FlowFixMe = createSelector(
  cabinBaggageCountSelector,
  getHandBagDefinitions,
  calculatePriceBaggage,
)

export const holdBagPriceCountSelector: $FlowFixMe = createSelector(
  holdBaggageCountSelector,
  getHoldBagDefinitions,
  calculatePriceBaggage,
)

// Total price
export const personalItemTotalPriceSelector: $FlowFixMe = createSelector(
  personalItemPriceCountSelector,
  sumBaggagePrices,
)

export const cabinBagTotalPriceSelector: $FlowFixMe = createSelector(
  cabinBagPriceCountSelector,
  sumBaggagePrices,
)

export const handBagTotalPriceSelector: $FlowFixMe = createSelector(
  personalItemTotalPriceSelector,
  cabinBagTotalPriceSelector,
  (personalItemTotalPrice, cabinBagTotalPrice) =>
    Price.sum([personalItemTotalPrice, cabinBagTotalPrice]),
)

export const holdBagTotalPriceSelector: $FlowFixMe = createSelector(
  holdBagPriceCountSelector,
  sumBaggagePrices,
)

export const baggageTotalPriceSelector: $FlowFixMe = createSelector(
  personalItemTotalPriceSelector,
  cabinBagTotalPriceSelector,
  holdBagTotalPriceSelector,
  Price.sum,
)

// =====
// Other

export const personalItemAllowedForAdultsSelector: $FlowFixMe = createSelector(
  getAdultHandBagCombinations,
  getHandBagDefinitions,
  (adultHandBagCombinations, handBagDefinitions) =>
    adultHandBagCombinations.some(combination =>
      combination.indices.some(
        bagIndex => handBagDefinitions[bagIndex].category === "personalItem",
      ),
    ),
)

export const cabinBagAllowedForAdultsSelector: $FlowFixMe = createSelector(
  getAdultHandBagCombinations,
  getHandBagDefinitions,
  (adultHandBagCombinations, handBagDefinitions) =>
    adultHandBagCombinations.some(combination =>
      combination.indices.some(bagIndex => handBagDefinitions[bagIndex].category === "cabinBag"),
    ),
)

export const holdBagAllowedForAdultsSelector: $FlowFixMe = createSelector(
  getAdultHoldBagCombinations,
  combinations => combinations.some(combination => combination.indices.length > 0),
)

export const holdBagAllowedByCategorySelector: $FlowFixMe = createSelector(
  getHoldBagCombinations,
  holdBagCombinations => (category: PassengerCategory) =>
    getGroupCombinations(category)(holdBagCombinations).some(
      combination => combination.indices.length > 0,
    ),
)

export const personalItemMinPriceForAdultsSelector: $FlowFixMe = createSelector(
  getHandBagDefinitions,
  // $FlowFixMe
  fp.compose(
    x => x || 0,
    fp.min,
    fp.map(fp.get(["price", "amount"])),
    fp.filter(def => def.category === "personalItem"),
  ),
)

export const cabinBagMinPriceForAdultsSelector: $FlowFixMe = createSelector(
  getHandBagDefinitions,
  // $FlowFixMe
  fp.compose(
    x => x || 0,
    fp.min,
    fp.map(fp.get(["price", "amount"])),
    fp.filter(def => def.category === "cabinBag"),
  ),
)

export const holdBagMinPriceForAdultsSelector: $FlowFixMe = createSelector(
  getHoldBagDefinitions,
  fp.compose(
    x => x || 0,
    fp.min,
    fp.map(fp.get(["price", "amount"])),
  ),
)

export const saveBookingBaggageSelector: $FlowFixMe = createSelector(
  passengersValuesSelector,
  getOriginalBaggage,
  (passengersValues, originalBaggage) =>
    mapperSaveBooking(passengersValues, originalBaggage.combinations),
)

export const bagsPriceDisclaimerSelector: $FlowFixMe = createSelector(
  getJourney,
  getAirlines,
  baggageTotalPriceSelector,
  (journey, airlines, bagsPrice) => {
    const airlineWithBagsDisclaimer = fp.compose(
      fp.map(airline => airlines[airline].name),
      fp.intersection(journey.getAirlines().toJS()),
    )(constants.airlinesWithBagsDisclaimer)

    if (airlineWithBagsDisclaimer.length > 1) {
      return {
        isEligible: Boolean(airlineWithBagsDisclaimer.length) && bagsPrice.amount > 0,
        tKey: __("booking.reservation_bill.bags_price_disclaimer_multiple"),
        values: {
          lastAirline: fp.tail(airlineWithBagsDisclaimer),
          airlines: fp.initial(airlineWithBagsDisclaimer).join(", "),
        },
      }
    }
    return {
      isEligible: Boolean(airlineWithBagsDisclaimer.length) && bagsPrice.amount > 0,
      tKey: __("booking.reservation_bill.bags_price_disclaimer"),
      values: {
        airline: airlineWithBagsDisclaimer,
      },
    }
  },
)
